"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.lookupIpChargeCorrectionQuerySchema = exports.requestIpChargeCategorySchema = exports.previewIpChargeCategorySchema = exports.MAX_IP_CATEGORY_CHANGE_LINES = exports.requestIpChargeCorrectionSchema = exports.previewIpChargeCorrectionSchema = exports.ipChargeCorrectionReplacementSchema = exports.ipChargeCorrectionKindEnum = exports.ipChargeLineCorrectionKindEnum = void 0;
/**
 * IP CHARGE CORRECTION REQUEST — the counter asks for ONE line of an inpatient bill to be corrected or deleted; a verifier and an approver decide
 * it (owner, 2026-10-14: "IP Correction sub tab required… BILL NUMBER should enter and all line items will show and in that each line having
 * option to ask for correction… should see all parameters including delete of that charge line. Then it should go same verify and approval flow").
 *
 * AN APPROVED LINE IS NEVER EDITED IN PLACE — the ledger is append-only (ADR-085). So the request is for one of two acts, and nothing moves until
 * an approver approves it:
 *
 *   CHANGE   the line is REVERSED and a corrected line is added, together, in one transaction;
 *   DELETE   the line is REVERSED and nothing replaces it. The original stays on record, marked Reversed.
 *
 * A CHANGE names the corrected line in full — the SAME inputs the Add line item dialog takes (the charge line, No of Days / Times, the rate of an
 * open-value service, the remarks, the operating team) — and the server prices it exactly as a capture would. Nothing in it is a price.
 */
const zod_1 = require("zod");
const ip_charge_schema_1 = require("../../schemas/ip-charge.schema");
const ip_pricing_schema_1 = require("../../schemas/ip-pricing.schema");
/** What a request on ONE line asks for. */
exports.ipChargeLineCorrectionKindEnum = zod_1.z.enum(['CHANGE', 'DELETE']);
/**
 * What a STORED request is: one of the two line kinds, or a CATEGORY change — several lines moved to another ward category (C1–C4) together
 * (owner, 2026-10-16: "Bulk Category change request… From Sl No and To Sl No… C1, C2, C3, C4… submit for correction").
 *
 *   CATEGORY   each line is REVERSED and the same line (service, count, remarks, operating team, an open-value rate) is added again PRICED AT THE
 *              NEW CATEGORY — all of them, in one transaction, or none.
 */
exports.ipChargeCorrectionKindEnum = zod_1.z.enum(['CHANGE', 'DELETE', 'CATEGORY']);
/**
 * The corrected line: the inputs of Add line item, and no source key (the server mints that when the request is approved).
 *
 * `tier` is the CATEGORY (C1-C4) the corrected line is priced at (owner, 2026-10-16: "under IP Correction need category change also, now it
 * is not selectable"). The dialog always sends it — the line's own category unless the clerk picks another — so a correction of the count
 * never quietly re-prices the line at the stay's category. A request stored before this carries none, and is priced as it always was.
 */
exports.ipChargeCorrectionReplacementSchema = ip_charge_schema_1.captureIpChargeSchema
    .pick({ serviceId: true, sourceType: true, unitRate: true, description: true, otDoctors: true, chargeGroup: true, tier: true })
    .extend({ quantity: zod_1.z.number().int().positive().max(10_000) });
/** What the preview takes: the request without the reason (the clerk checks the effect before writing why). */
exports.previewIpChargeCorrectionSchema = zod_1.z.object({
    ledgerId: zod_1.z.string().uuid(),
    kind: exports.ipChargeLineCorrectionKindEnum,
    /** Required for CHANGE, refused for DELETE — the service says which, in words. */
    replacement: exports.ipChargeCorrectionReplacementSchema.optional(),
});
exports.requestIpChargeCorrectionSchema = exports.previewIpChargeCorrectionSchema.extend({
    reason: zod_1.z.string().trim().min(3, 'A reason is required').max(500),
});
exports.MAX_IP_CATEGORY_CHANGE_LINES = 60;
/**
 * BULK CATEGORY CHANGE — the lines the desk chose (the tab turns its Sl No range into the lines' ids), and the category they should be priced at.
 * Nothing about a price is sent: the server re-prices each line itself, at that category, exactly as a capture would.
 */
exports.previewIpChargeCategorySchema = zod_1.z.object({
    ledgerIds: zod_1.z
        .array(zod_1.z.string().uuid())
        .min(1, 'Choose at least one line')
        .max(exports.MAX_IP_CATEGORY_CHANGE_LINES)
        .refine((ids) => new Set(ids).size === ids.length, 'A line is named twice'),
    tier: ip_pricing_schema_1.ipChargeTierEnum,
});
exports.requestIpChargeCategorySchema = exports.previewIpChargeCategorySchema.extend({
    reason: zod_1.z.string().trim().min(3, 'A reason is required').max(500),
});
/** Step one of the IP Correction tab: the bill number the desk is holding. */
exports.lookupIpChargeCorrectionQuerySchema = zod_1.z.object({
    billNumber: zod_1.z.string().trim().min(1, 'Enter a bill number').max(30),
});
//# sourceMappingURL=financial-ip-charge-correction.request.js.map